import React, { useState } from "react";
import { Loader2, CheckCircle2, AlertTriangle, Rocket, Globe, Zap, Target } from "lucide-react";
import { db } from "../firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";
import { useTranslation } from "../useTranslation";

const Startups = () => {
  const { t } = useTranslation();
  const [status, setStatus] = useState({ state: "idle" });
  const [form, setForm] = useState({
    email: "",
    fullName: "",
    companyName: "",
    website: "",
    industry: "",
    description: "",
    travelFrequency: "",
    consent: false,
  });

  // Generate unique discount code
  const generateReferralCode = () => {
    const companyPrefix = form.companyName
      .replace(/[^a-zA-Z0-9]/g, '')
      .toUpperCase()
      .substring(0, 4);
    const randomSuffix = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `STARTUP${companyPrefix}${randomSuffix}`;
  };

  // Validation helpers
  const emailOk = /\S+@\S+\.\S+/.test(form.email);
  const nameOk = form.fullName.trim()?.length >= 2;
  const companyOk = form.companyName.trim()?.length >= 2;
  const industryOk = form.industry?.length > 0;
  const travelFrequencyOk = form.travelFrequency?.length > 0;
  const consentOk = form.consent;

  const disabled = !emailOk || !nameOk || !companyOk || !industryOk || !travelFrequencyOk || !consentOk || status.state === "loading";

  const COMPANY_SIZES = [
    "1-10 employees",
    "11-50 employees", 
    "51-200 employees",
    "201-500 employees",
    "500+ employees"
  ];

  const INDUSTRIES = [
    "Technology/Software",
    "E-commerce/Retail",
    "Finance/Fintech",
    "Healthcare/Biotech",
    "Travel/Tourism",
    "Media/Entertainment",
    "Education/EdTech",
    "Real Estate",
    "Consulting",
    "Manufacturing",
    "Other"
  ];

  const TRAVEL_FREQUENCIES = [
    "Weekly",
    "Monthly", 
    "Quarterly",
    "2-3 times per year",
    "Once per year",
    "Rare/as needed"
  ];

  const onSubmit = async (e) => {
    e.preventDefault();
    if (disabled) return;

    try {
      setStatus({ state: "loading" });
      
      const referralCode = generateReferralCode();
      
      const payload = {
        ...form,
        referralCode,
        discountPercentage: 30,
        status: "approved", // Auto-approve startup applications
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : "",
        createdAt: serverTimestamp(),
      };

      await addDoc(collection(db, "startups"), payload);
      
      setStatus({ 
        state: "success", 
        referralCode 
      });
      
      // Reset form
      setForm({
        email: "",
        fullName: "",
        companyName: "",
        website: "",
        industry: "",
        description: "",
        travelFrequency: "",
        consent: false,
      });
    } catch (err) {
      console.error("Startup application error", err);
      setStatus({ 
        state: "error", 
        message: err?.message || "Something went wrong" 
      });
    }
  };

  const resetForm = () => {
    setStatus({ state: "idle" });
    setForm({
      email: "",
      fullName: "",
      companyName: "",
      website: "",
      industry: "",
      description: "",
      travelFrequency: "",
      consent: false,
    });
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Hero Section */}
      <div className="bg-white pt-20 pb-10">
        <div className="max-w-6xl mx-auto px-6 text-center">
          <p className="text-purple-500 text-sm font-semibold uppercase tracking-wide mb-4">
            Startup Program
          </p>
          <h1 className="text-4xl font-bold text-gray-900 mb-6">
            30% Off for <span className="text-purple-500">Startups</span>
          </h1>
          <p className="text-lg text-gray-600 mb-12 max-w-3xl mx-auto">
            We understand the challenges of building a global business. Get exclusive 30% discount on all Airvoy eSIM plans to help your team stay connected worldwide while you focus on growth.
          </p>
        </div>
      </div>

      {/* Application Form */}
      <div id="application-form" className="pb-20 pt-6 bg-white">
        <div className="max-w-3xl mx-auto px-6">
          <div className="bg-gray-50 rounded-2xl p-8">
            {status.state === "success" ? (
              <div className="text-center py-8">
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Application Submitted Successfully!
                </h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  Thank you for applying to our startup program. We've received your application and will review it within 1-2 business days.
                </p>
                <button
                  onClick={resetForm}
                  className="bg-purple-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-600 transition-colors"
                >
                  Submit Another Application
                </button>
              </div>
            ) : status.state === "error" ? (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mb-6 mx-auto">
                  <AlertTriangle className="w-10 h-10 text-red-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">
                  Application Failed
                </h3>
                <p className="text-gray-600 mb-8 max-w-md mx-auto">
                  {status.message || "Something went wrong while submitting your application. Please try again."}
                </p>
                <button
                  onClick={() => setStatus({ state: "idle" })}
                  className="bg-purple-500 text-white px-8 py-3 rounded-lg font-semibold hover:bg-purple-600 transition-colors"
                >
                  Try Again
                </button>
              </div>
            ) : (
              <>
                <div className="text-center mb-8">
                  <div className="w-16 h-16 bg-purple-100 rounded-2xl flex items-center justify-center mb-4 mx-auto">
                    <Rocket className="w-8 h-8 text-purple-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-2">Apply for Startup Discount</h2>
                  <p className="text-gray-600">Tell us about your startup and get your unique discount code (Valid for 12 months)</p>
                </div>

                <form onSubmit={onSubmit} className="space-y-6">
                  {/* Personal Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        placeholder="John Doe"
                        value={form.fullName}
                        onChange={(e) => setForm(f => ({ ...f, fullName: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        placeholder="john@startup.com"
                        value={form.email}
                        onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      />
                    </div>
                  </div>

                  {/* Company Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        placeholder="Amazing Startup Inc."
                        value={form.companyName}
                        onChange={(e) => setForm(f => ({ ...f, companyName: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Website (Optional)
                      </label>
                      <input
                        type="url"
                        placeholder="https://amazingstartup.com"
                        value={form.website}
                        onChange={(e) => setForm(f => ({ ...f, website: e.target.value }))}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                      />
                    </div>
                  </div>

                  {/* Company Details */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Company Size *
                      </label>
                      <div className="relative">
                        <select
                          value={form.companySize}
                          onChange={(e) => setForm(f => ({ ...f, companySize: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white text-gray-700"
                          required
                        >
                          <option value="">Select company size</option>
                          {COMPANY_SIZES.map((size) => (
                            <option key={size} value={size}>{size}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Industry *
                      </label>
                      <div className="relative">
                        <select
                          value={form.industry}
                          onChange={(e) => setForm(f => ({ ...f, industry: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white text-gray-700"
                          required
                        >
                          <option value="">Select industry</option>
                          {INDUSTRIES.map((industry) => (
                            <option key={industry} value={industry}>{industry}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Travel Information */}
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Team Travel Frequency *
                      </label>
                      <div className="relative">
                        <select
                          value={form.travelFrequency}
                          onChange={(e) => setForm(f => ({ ...f, travelFrequency: e.target.value }))}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent appearance-none bg-white text-gray-700"
                          required
                        >
                          <option value="">Select frequency</option>
                          {TRAVEL_FREQUENCIES.map((freq) => (
                            <option key={freq} value={freq}>{freq}</option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                          <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Company Description */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Tell us about your startup (Optional)
                    </label>
                    <textarea
                      placeholder="Brief description of what your startup does, your stage, and how global connectivity helps your business..."
                      value={form.description}
                      onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
                    />
                  </div>

                  {/* Consent */}
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="consent"
                      checked={form.consent}
                      onChange={(e) => setForm(f => ({ ...f, consent: e.target.checked }))}
                      className="w-4 h-4 text-purple-500 focus:ring-purple-500 border-gray-300 rounded mt-1"
                      required
                    />
                    <label htmlFor="consent" className="text-sm text-gray-600">
                      I agree to Airvoy's{" "}
                      <a href="/privacy" target="_blank" className="text-purple-500 hover:underline">
                        Privacy Policy
                      </a>{" "}
                      and{" "}
                      <a href="/terms-of-service" target="_blank" className="text-purple-500 hover:underline">
                        Terms of Service
                      </a>
                      . I understand that this discount is for startup use only and may be subject to verification. *
                    </label>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={disabled}
                    className="w-full bg-purple-500 text-white py-4 px-6 rounded-lg font-semibold hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  >
                    {status.state === "loading" ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Processing Application...
                      </>
                    ) : (
                      <>
                        <Rocket className="w-5 h-5" />
                        Submit Application
                      </>
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    Applications are typically reviewed within 1-2 business days. If approved, your discount code will be sent to your email.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Additional Info Section */}
      <div className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h3 className="text-2xl font-bold text-gray-900 mb-8">Frequently Asked Questions</h3>
          
          <div className="grid md:grid-cols-2 gap-8 text-left">
            <div className="bg-white rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Who qualifies for the startup discount?</h4>
              <p className="text-gray-600 text-sm">
                Early-stage companies (typically less than 5 years old) with less than 500 employees that can demonstrate a legitimate business use for international connectivity.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">How long is the discount valid?</h4>
              <p className="text-gray-600 text-sm">
                Your discount code is valid for 12 months from the date of issue. After that, standard pricing applies.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">Can I share my discount code?</h4>
              <p className="text-gray-600 text-sm">
                The code is intended for your startup's use only. Sharing with other companies or misuse may result in code deactivation.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6">
              <h4 className="text-lg font-semibold text-gray-900 mb-3">What if I need help with my application?</h4>
              <p className="text-gray-600 text-sm">
                Contact our support team at support@airvoy.com if you have questions about the program or need assistance with your application.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Startups;